import axios from 'axios';
import { API_CONFIG, AUTH_CONFIG } from './config';

/**
 * Generate a unique session ID
 * @returns {string} A unique session ID
 */
export const generateSessionId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Get or create a session ID
 * @returns {string} The current session ID
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem(AUTH_CONFIG.SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);
  }
  return sessionId;
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_CONFIG.API_URL,
  headers: API_CONFIG.DEFAULT_HEADERS,
  timeout: API_CONFIG.TIMEOUT,
});

// Track if we're currently refreshing the page to prevent redirect loops
let isPageRefresh = false;

// Set the refresh flag when the page is loading
if (document.readyState !== 'complete') {
  isPageRefresh = true;
  // Reset the flag after the page has fully loaded
  window.addEventListener('load', () => {
    setTimeout(() => {
      isPageRefresh = false;
    }, 1000); // Give a small delay to ensure all initial requests are handled as part of the refresh
  });
}

// Add request interceptor to include auth token in requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      // Add session ID to all authenticated requests
      config.headers['X-Session-ID'] = getSessionId();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip auth error handling during page refresh
    if (isPageRefresh && error.response && error.response.status === 401) {
      console.log('Ignoring auth error during page refresh');
      return Promise.reject(error);
    }
    
    // Handle session invalidated error
    if (error.response && error.response.status === 401) {
      // Only handle auth errors for actual API requests, not for auth checks
      if (error.config.url.includes('/auth/current-user')) {
        console.log('Auth check failed, but not redirecting during page initialization');
        return Promise.reject(error);
      }
      
      if (error.response.data && error.response.data.code === 'SESSION_INVALIDATED') {
        // Clear auth data
        localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
        localStorage.removeItem(AUTH_CONFIG.USER_KEY);
        localStorage.removeItem(AUTH_CONFIG.TENANT_KEY);
        localStorage.removeItem(AUTH_CONFIG.SESSION_KEY);
        
        // Show message to user
        alert('Your session has been invalidated because you logged in from another device or browser. Please log in again.');
        
        // Only redirect to login if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      // For other 401 errors on regular API calls (not during refresh or auth check)
      console.log('Authentication error on API call:', error.config.url);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
